import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { ITEM_SPACING, PROJECT_COUNT } from '../constants';

// Fix R3F intrinsic element type errors by using type-safe component wrappers
const ThreeLine = 'line' as any;
const ThreeGroup = 'group' as any;
const ThreeLineBasicMaterial = 'lineBasicMaterial' as any;
const ThreePoints = 'points' as any;
const ThreeBufferGeometry = 'bufferGeometry' as any;
const ThreeBufferAttribute = 'bufferAttribute' as any;
const ThreePointsMaterial = 'pointsMaterial' as any;

export const Spine: React.FC<{ velocityRef: React.MutableRefObject<number> }> = ({ velocityRef }) => {
  const lineRef = useRef<THREE.Line>(null);
  const particlesRef = useRef<THREE.Points>(null);
  
  const totalWidth = PROJECT_COUNT * ITEM_SPACING;
  const pointsCount = 150; // Increased resolution for smoother curve
  // Match Radius from Card.tsx for visual consistency
  const radius = 7.0; 

  const points = useMemo(() => {
    const p = [];
    for (let i = 0; i <= pointsCount; i++) {
      // Draw slightly wider than visible range
      const xLinear = (i / pointsCount - 0.5) * totalWidth * 1.8;
      
      const angle = xLinear / radius;
      const x = radius * Math.sin(angle);
      const z = radius * (Math.cos(angle) - 1);
      
      // Organic Y-axis flow:
      // Creates a subtle wave that dips in the center and rises slightly at edges, 
      // mimicking a structural spine supporting the cards.
      const y = -2.4 - Math.cos(angle * 0.8) * 0.4 + Math.sin(angle * 2.0) * 0.1;

      p.push(new THREE.Vector3(x, y, z));
    }
    return p;
  }, [totalWidth, radius]);

  // 'centripetal' creates a smoother curve suitable for 3D paths
  const curve = useMemo(() => new THREE.CatmullRomCurve3(points, false, 'centripetal', 0.5), [points]);
  const geometry = useMemo(() => new THREE.BufferGeometry().setFromPoints(curve.getPoints(400)), [curve]);

  const [particlesPos, particlesSizes] = useMemo(() => {
    const count = 600; // Increased particle count
    const pos = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    
    for (let i = 0; i < count; i++) {
      const t = Math.random();
      const point = curve.getPoint(t);
      // Increased spread for a more volumetric feel
      const spread = 0.6; 
      
      pos[i * 3] = point.x + (Math.random() - 0.5) * spread;
      pos[i * 3 + 1] = point.y + (Math.random() - 0.5) * spread * 0.5; // Flatter vertical spread
      pos[i * 3 + 2] = point.z + (Math.random() - 0.5) * spread;
      
      sizes[i] = Math.random() * 1.5;
    }
    return [pos, sizes];
  }, [curve]);

  useFrame((state) => {
    if (!lineRef.current || !particlesRef.current) return;
    
    const v = Math.abs(velocityRef.current) * 0.15;
    const time = state.clock.elapsedTime;

    // Animate Line Material
    const material = lineRef.current.material as THREE.LineBasicMaterial;
    // Pulse opacity based on velocity
    material.opacity = THREE.MathUtils.lerp(material.opacity, 0.3 + v, 0.05);
    
    // Rotate particles slowly
    particlesRef.current.rotation.y = time * 0.02;
    
    // Breathing scale effect
    const pulse = Math.sin(time * 1.5) * 0.05 + 1.0;
    lineRef.current.scale.set(1, pulse, 1);
    
    // Subtle wave animation on particles? 
    // (Requires shader material for performance, keeping simple for now)
  });

  return (
    <ThreeGroup>
      {/* Core Spine Line */}
      <ThreeLine ref={lineRef} geometry={geometry}>
        <ThreeLineBasicMaterial color="#ffffff" transparent opacity={0.4} linewidth={2} />
      </ThreeLine>
      
      {/* Outer Glow Line */}
      <ThreeLine geometry={geometry} scale={[1, 1.02, 1]} position={[0, -0.05, 0]}>
        <ThreeLineBasicMaterial color="#4444ff" transparent opacity={0.15} linewidth={3} />
      </ThreeLine>

      {/* Floating Particles */}
      <ThreePoints ref={particlesRef}>
        <ThreeBufferGeometry>
          <ThreeBufferAttribute
            attach="attributes-position"
            count={particlesPos.length / 3}
            array={particlesPos}
            itemSize={3}
          />
          <ThreeBufferAttribute
            attach="attributes-size"
            count={particlesSizes.length}
            array={particlesSizes}
            itemSize={1}
          />
        </ThreeBufferGeometry>
        <ThreePointsMaterial
          size={0.02}
          color="#8888ff"
          transparent
          opacity={0.5}
          sizeAttenuation={true}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </ThreePoints>
    </ThreeGroup>
  );
};